import {
  ASSET_MANAGER,
  LOCUS_CUSTOMER_USER_MANAGER,
  LOCUS_TENANT,
  LOCUS_USER_MANAGER,
  SUPER_ADMIN,
  TOOLS_USER,
  USER_MANAGER,
  SYSTEM_INTEGRATOR
} from "../../utils/constants/user";
import { hasPermissions } from "../../utils/helpers/permissions";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

// https://locusrobotics.atlassian.net/browse/HUB-2368
const getCreateUserPermissionsByViewState = (viewState) => {
  if (viewState === "locus") {
    return [SUPER_ADMIN, LOCUS_USER_MANAGER];
  } else {
    return [SUPER_ADMIN, LOCUS_CUSTOMER_USER_MANAGER, USER_MANAGER];
  }
};

export const getUserListActionButtons = ({ activeUser, viewState }) => {
  return [
    {
      name: "Add User",
      action: "create",
      // https://locusrobotics.atlassian.net/browse/HUB-2344
      allowedGroups: getCreateUserPermissionsByViewState(viewState),
      viewState: ["locus", "client"],
    },
  ];
};

/* USER_MANAGERs cannot assign permissions to themselves */
export const isUserManagerEditingSelf = (activeUser, userIdToEdit) => {
  return (
    activeUser?.id === userIdToEdit &&
    hasPermissions(
      [USER_MANAGER, LOCUS_USER_MANAGER, LOCUS_CUSTOMER_USER_MANAGER],
      activeUser,
    )
  );
};

/* checks if user has combo of roles to allow permission edit SYSTEM_INTEGRATOR */
export const comboRequiredGroupsOverride = (activeUser, activeGroup, viewState, client) => {
  return (
    hasPermissions(
      [SUPER_ADMIN],
      activeUser,
    ) ||
    (
      hasPermissions(
        [LOCUS_CUSTOMER_USER_MANAGER],
        activeUser,
      ) &&
      hasPermissions(
        [SYSTEM_INTEGRATOR],
        activeUser,
      ) &&
      activeGroup?.name === SYSTEM_INTEGRATOR &&
      viewState === "client" &&
      (client ? client?.domain === "sysis" : false)
    )
  );
};

/* Locus User Managers & Customer User Managers cannot assign permissions to another USER_MANAGER type */
export const isUserManagerEditingAnother = (
  activeUser,
  selectedUser,
  viewState,
) => {
  if (hasPermissions([SUPER_ADMIN], activeUser)) {
    return false;
  }

  if (
    (viewState === "client" &&
      hasPermissions([LOCUS_CUSTOMER_USER_MANAGER], activeUser) &&
      hasPermissions([LOCUS_CUSTOMER_USER_MANAGER], selectedUser)) ||
    (hasPermissions([LOCUS_USER_MANAGER], activeUser) &&
      hasPermissions([LOCUS_USER_MANAGER], selectedUser)) ||
    (hasPermissions([USER_MANAGER], activeUser) &&
      hasPermissions([USER_MANAGER], selectedUser))
  ) {
    return true;
  }
  return false;
};

export const isStandardPermissionsRestrictedByPermissions = (
  activeUser,
  viewState,
) => {
  // Super Admin has no restrictions
  if (hasPermissions([SUPER_ADMIN], activeUser)) {
    return false;
  }

  if (
    (
      viewState === "client" &&
      hasPermissions([LOCUS_CUSTOMER_USER_MANAGER], activeUser)
    ) ||
    hasPermissions([USER_MANAGER], activeUser)
  ) {
    return false;
  }

  /* Prevent `LOCUS_CUSTOMER_USER_MANAGER` && `USER_MANAGER from editing locus users */
  if (
    viewState === "locus" &&
    hasPermissions([LOCUS_USER_MANAGER], activeUser)
  ) {
    return false;
  }
  return true;
};

export const isGroupRestrictedByPermissions = (group, user) => {
  // Super Admin has no restrictions
  if (hasPermissions([SUPER_ADMIN], user)) {
    return false;
  }

  /* Prevent user manager types from editing user managers */
  if (
    group?.name === LOCUS_USER_MANAGER &&
    hasPermissions([LOCUS_USER_MANAGER, USER_MANAGER], user)
  ) {
    return true;
  }

  if (
    group?.name === LOCUS_CUSTOMER_USER_MANAGER &&
    hasPermissions([LOCUS_CUSTOMER_USER_MANAGER, USER_MANAGER], user) &&
    !hasPermissions([LOCUS_USER_MANAGER], user)
  ) {
    return true;
  }

  /* Prevent USER_MANAGER from assigning USER_MANAGER to other client users */
  if (
    group?.name === USER_MANAGER &&
    hasPermissions([USER_MANAGER], user) &&
    !hasPermissions([LOCUS_CUSTOMER_USER_MANAGER, LOCUS_USER_MANAGER], user)
  ) {
    return true;
  }

  /* Prevent `LOCUS_CUSTOMER_USER_MANAGER`s from editing locus users and allow LOCUS_USER_MANAGER HUB-2971 */
  if (
    group?.name === LOCUS_TENANT &&
    hasPermissions([LOCUS_CUSTOMER_USER_MANAGER], user) &&
    !hasPermissions([LOCUS_USER_MANAGER], user)
  ) {
    return true;
  }

  /* Only LOCUS_CUSTOMER_USER_MANAGER and USER_MANAGER can assign ASSET_MANAGER */
  if (
    (group?.name === ASSET_MANAGER || group?.name === TOOLS_USER) &&
    !hasPermissions([LOCUS_CUSTOMER_USER_MANAGER, USER_MANAGER], user)
  ) {
    return true;
  }

  return false;
};
        // valueGetter: (lastSeenAt) => {
        //   if (!lastSeenAt) return "N/A";
          
        //   // Convert UTC to user's timezone (if available)
        //   return dayjs(lastSeenAt)
        //     .tz(activeUser?.timezone || dayjs.tz.guess())
        //     .format("MMM DD YYYY, HH:mm");
        // },
        
// Normalizes a groups array (objects or strings) into group name strings.
const normalizeGroupNames = (groups) =>
  (groups || []).map((item) => (typeof item === "string" ? item : item?.name));

// Returns true if the group's `requires` are not all present in the provided groups.
export const isRequiredGroupMissing = (group, groups) => {
  if (!group?.requires || group.requires.length === 0) return false;
  const names = normalizeGroupNames(groups);
  return !group.requires.every((req) => names.includes(req));
};

// Returns true if any of the group's `preventDisable` dependents are currently present.
export const hasPreventDisableDependents = (group, groups) => {
  if (!group?.preventDisable || group.preventDisable.length === 0) return false;
  const names = normalizeGroupNames(groups);
  return group.preventDisable.some((dep) => names.includes(dep));
};

// Returns the names of active dependent groups (via preventDisable) for cascade-removal.
export const getDependentGroupNames = (group, groups) => {
  if (!group?.preventDisable || group.preventDisable.length === 0) return [];
  const names = normalizeGroupNames(groups);
  return group.preventDisable.filter((dep) => names.includes(dep));
};

// Filters user's groups to only show those that are advanced permissions (for read-only display).
// Uses the full defaultAdvanced list (locus + client combined) so that all advanced roles are visible
// regardless of the current viewState.
export const getDisplayAdvancedGroups = (userGroups, defaultAdvanced) => {
  if (!userGroups || !defaultAdvanced) return [];
  return userGroups.filter((item) =>
    defaultAdvanced.some((o) => o.name === item.name)
  );
};

export const formatLastSeenAtValue = (lastSeenAt, activeTz) => {
  if (!lastSeenAt) return "N/A";
  
  // Convert UTC to user's timezone (if available)
  return dayjs(lastSeenAt)
  .tz(activeTz || dayjs.tz.guess())
  .format("MMM DD YYYY, HH:mm");
}